<template>
  <div class="chat-container">

    <!-- Header del agente -->
    <div class="chat-header">
      <div class="header-left">
        <div :class="['agent-avatar', agentCategory]">
          <i :class="['pi', categoryIcon]" />
        </div>
        <div>
          <h2>{{ agent?.name ?? 'Agente' }}</h2>
          <span class="agent-desc">{{ agent?.description?.slice(0, 80) }}</span>
        </div>
      </div>
      <div class="header-right">
        <Tag :value="agent?.category" severity="info" v-if="agent?.category" />
        <Button
          v-if="financialResults"
          icon="pi pi-chart-bar"
          severity="success"
          text
          rounded
          v-tooltip="'Ver último análisis'"
          @click="showResultsDialog = true"
        />
        <Button
          v-if="supportsDocuments"
          :icon="showUpload ? 'pi pi-times' : 'pi pi-paperclip'"
          :severity="showUpload ? 'secondary' : 'info'"
          text
          rounded
          v-tooltip="showUpload ? 'Cerrar' : 'Adjuntar documento'"
          @click="showUpload = !showUpload"
        />
        <Button
          v-if="isFinancialAgent"
          icon="pi pi-folder-open"
          severity="help"
          text
          rounded
          v-tooltip="'Buscar en Centro Documental'"
          @click="openRepositorySearch"
        />
      </div>
    </div>

    <div class="context-strip">
      <div v-if="sessionDocuments.length" class="context-item">
        <i class="pi pi-folder-open" />
        <span>{{ sessionDocuments.length }} documento(s) en sesión</span>
      </div>
      <div v-if="financialResults" class="context-item context-item-success">
        <i class="pi pi-check-circle" />
        <span>Último análisis: {{ financialResults.filename }}</span>
        <Button icon="pi pi-external-link" text size="small" @click="showResultsDialog = true" />
      </div>
    </div>

    <div v-if="analysisTasks.length" class="tasks-strip">
      <div class="tasks-strip-header">
        <span class="tasks-strip-title">Tareas de análisis</span>
        <Tag :value="`${activeTasksCount} activa(s)`" :severity="activeTasksCount ? 'warning' : 'success'" />
      </div>
      <div class="tasks-list">
        <div v-for="task in analysisTasks" :key="task.taskId" class="task-item">
          <div class="task-main">
            <span class="task-file">{{ task.filename || `Documento #${task.documentId || '-'}` }}</span>
            <Tag :value="task.status" :severity="taskStatusSeverity(task.status)" size="small" />
          </div>
          <div class="task-sub">
            <span>{{ task.message || 'Procesando...' }}</span>
            <span>{{ task.progressPercent }}%</span>
          </div>
          <ProgressBar :value="task.progressPercent" />
        </div>
      </div>
    </div>

    <!-- Área de mensajes -->
    <div class="messages" ref="messagesEl">
      <!-- Mensaje de bienvenida -->
      <div v-if="messages.length === 0" class="welcome-msg">
        <div :class="['welcome-avatar', agentCategory]">
          <i :class="['pi', categoryIcon]" />
        </div>
        <div class="welcome-text">
          <strong>{{ agent?.name }}</strong>
          <p>{{ welcomeText }}</p>
        </div>
      </div>

      <!-- Mensajes -->
      <div
        v-for="(msg, i) in messages"
        :key="i"
        :class="['message', msg.isUser ? 'user' : 'agent']"
      >
        <!-- Mensaje de documento subido -->
        <div v-if="msg.type === 'document'" class="bubble doc-bubble">
          <i :class="['pi', docIcon(msg.fileType)]" />
          <span>{{ msg.text }}</span>
        </div>

        <!-- Mensaje de texto normal -->
        <div v-else class="bubble">
          <div v-html="msg.isUser ? escapeHtml(msg.text) : renderMarkdown(msg.text)" />
          <div v-if="msg.downloads?.length" class="chat-downloads">
            <button
              v-for="(download, di) in msg.downloads"
              :key="`${download.report_id || di}-${download.filename || 'archivo'}`"
              class="chat-download-btn"
              type="button"
              @click="downloadFromChat(download)"
            >
              <i class="pi pi-download" />
              <span>{{ download.filename || `Reporte ${download.report_id}` }}</span>
            </button>
          </div>
        </div>

        <div class="meta">
          <span v-if="msg.tokens">{{ msg.tokens }} tokens</span>
          <span v-if="msg.time">{{ msg.time }}</span>
        </div>
      </div>

      <!-- Indicador de escritura -->
      <div v-if="loading" class="message agent">
        <div class="bubble typing">
          <span class="dot" /><span class="dot" /><span class="dot" />
        </div>
      </div>
    </div>

    <!-- Input de chat -->
    <div class="chat-input">
      <!-- Indicador de documento activo -->
      <div v-if="activeDocumentId && isFinancialAgent" class="active-doc-bar">
        <i class="pi pi-file-excel" />
        <span>Documento activo: <strong>{{ activeDocumentName }}</strong></span>
        <button class="clear-doc" @click="activeDocumentId = null" title="Quitar documento del contexto">
          <i class="pi pi-times" />
        </button>
      </div>
      <!-- Indicador de grabación -->
      <div v-if="isRecording" class="recording-indicator">
        <span class="recording-dot" />
        <span>Grabando... {{ formatRecordDuration(recordDuration) }}</span>
        <button class="recording-cancel" @click="cancelRecording" title="Cancelar">
          <i class="pi pi-times" />
        </button>
      </div>
      <!-- Indicador de transcripción -->
      <div v-if="isTranscribing" class="recording-indicator">
        <i class="pi pi-spin pi-spinner" />
        <span>Transcribiendo audio...</span>
      </div>
      <!-- Error de grabación -->
      <div v-if="recordError" class="record-error">
        <i class="pi pi-exclamation-triangle" />
        <span>{{ recordError }}</span>
        <button class="record-error-close" @click="recordError = null">
          <i class="pi pi-times" />
        </button>
      </div>
      <div class="input-row">
        <Textarea
          v-model="input"
          :placeholder="isRecording ? 'Escuchando...' : inputPlaceholder"
          :disabled="loading || isRecording"
          :autoResize="true"
          rows="1"
          class="chat-textarea"
          @keydown.enter.exact.prevent="sendMessage"
          @keydown.enter.shift.exact="input += '\n'"
        />
        <div class="input-actions">
          <Button
            :icon="isRecording ? 'pi pi-stop' : 'pi pi-microphone'"
            :severity="isRecording ? 'danger' : 'secondary'"
            :disabled="loading || isTranscribing || !canRecord"
            :loading="isTranscribing"
            @click="isRecording ? handleStopRecording() : handleStartRecording()"
            text
            rounded
            outlined
            v-tooltip="isRecording ? 'Detener y transcribir' : 'Dictar por voz'"
          />
          <Button
            icon="pi pi-send"
            rounded
            :disabled="!input.trim() || loading"
            :loading="loading"
            @click="sendMessage"
          />
        </div>
      </div>
      <div class="input-hint">
        Enter para enviar · Shift+Enter para nueva línea
        <span v-if="supportsDocuments"> · 📎 para adjuntar archivos</span>
      </div>
    </div>

    <!-- Voice input removed: now using inline dictation via faster-whisper -->

    <Dialog
      v-model:visible="showUpload"
      :header="isFinancialAgent ? 'Subir estado financiero para análisis' : 'Adjuntar documento al agente'"
      modal
      :draggable="false"
      :style="{ width: 'min(940px, 96vw)' }"
    >
      <DocumentUpload
        :agent-id="agentId"
        :agent-category="agentCategory"
        :accept="allowedFormats"
        :upload-label="isFinancialAgent ? 'Subir y analizar' : 'Subir documento'"
        :upload-icon="isFinancialAgent ? 'pi pi-chart-bar' : 'pi pi-upload'"
        :analysis-timeout-ms="900000"
        :use-async-financial-analysis="isFinancialAgent"
        :task-poll-interval-ms="2500"
        @uploaded="onDocumentUploaded"
        @analyzed="onDocumentAnalyzed"
        @error="onUploadError"
        @task-queued="onTaskQueued"
        @task-progress="onTaskProgress"
        @task-completed="onTaskCompleted"
        @task-failed="onTaskFailed"
      />
      <div v-if="sessionDocuments.length" class="session-docs">
        <p class="session-docs-title">Documentos en esta sesión:</p>
        <div v-for="doc in sessionDocuments" :key="doc.id" class="session-doc-item">
          <i :class="['pi', docIcon(doc.file_type)]" />
          <span>{{ doc.filename }}</span>
          <Tag :value="doc.status" :severity="doc.status === 'processed' ? 'success' : 'info'" size="small" />
        </div>
      </div>
    </Dialog>

    <Dialog
      v-model:visible="showResultsDialog"
      header="Resultados del análisis financiero"
      modal
      :draggable="false"
      maximizable
      :style="{ width: 'min(1200px, 98vw)' }"
    >
      <FinancialResults v-if="financialResults" :data="financialResults" />
    </Dialog>

    <Dialog
      v-model:visible="showRepositorySearch"
      header="Buscar documento en Centro Documental"
      modal
      :draggable="false"
      :style="{ width: 'min(900px, 96vw)' }"
    >
      <section class="repo-search">
        <div class="repo-search__bar">
          <InputText
            v-model.trim="repoSearchQuery"
            class="repo-search__input"
            placeholder="Buscar por ID, título o archivo..."
            @input="onRepositorySearchInput"
          />
          <Button
            label="Buscar"
            icon="pi pi-search"
            :loading="repoSearchLoading"
            @click="runRepositorySearch"
          />
        </div>

        <p v-if="repoSearchError" class="repo-search__error">{{ repoSearchError }}</p>
        <p v-else-if="repoSearchLoading" class="repo-search__hint">Buscando documentos...</p>

        <div v-else class="repo-search__results">
          <article
            v-for="doc in repoSearchResults"
            :key="doc.document_id"
            class="repo-doc-item"
          >
            <div class="repo-doc-item__meta">
              <strong>#{{ doc.document_id }} · {{ doc.title }}</strong>
              <span>{{ doc.source_filename || 'Sin nombre de archivo' }}</span>
              <small>Estado: {{ doc.status }} · Versión actual: v{{ doc.current_version_number ?? '—' }}</small>
            </div>
            <Button
              label="Usar en chat"
              size="small"
              icon="pi pi-check"
              :loading="selectingRepositoryDocumentId === doc.document_id"
              :disabled="Boolean(selectingRepositoryDocumentId && selectingRepositoryDocumentId !== doc.document_id)"
              @click="useRepositoryDocument(doc)"
            />
          </article>

          <p v-if="!repoSearchResults.length" class="repo-search__hint">
            No se encontraron documentos para este criterio.
          </p>
        </div>
      </section>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useAuthStore } from '../../stores/auth';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Textarea from 'primevue/textarea';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { useAudioRecorder } from '../../composables/useAudioRecorder';
import DocumentUpload from '../common/DocumentUpload.vue';
import FinancialResults from '../financial/FinancialResults.vue';
import { agentsService } from '../../services/agents';
import { financialService } from '../../services/documents';
import api from '../../services/api';
import {
  listRepositoryDocuments,
  resolveRepositoryDocumentChatContext,
} from '../../services/documentRepository';

const props = defineProps({
  agentId: { type: Number, required: true },
});

const authStore = useAuthStore();

// Estado
const agent = ref(null);
const messages = ref([]);
const input = ref('');
const loading = ref(false);
const showUpload = ref(false);
const showResultsDialog = ref(false);
const showRepositorySearch = ref(false);
const financialResults = ref(null);
const sessionDocuments = ref([]);
const financialSessionId = ref(null);
const activeDocumentId = ref(null);   // ID del último documento analizado en la sesión
const messagesEl = ref(null);
const analysisTasks = ref([]);
const repoSearchQuery = ref('');
const repoSearchLoading = ref(false);
const repoSearchError = ref('');
const repoSearchResults = ref([]);
const selectingRepositoryDocumentId = ref(null);
let tasksPollIntervalId = null;
let repositorySearchDebounceId = null;

// Audio recorder (faster-whisper)
const {
  isRecording,
  isTranscribing,
  recordingDuration: recordDuration,
  error: recordError,
  canRecord,
  startRecording,
  stopAndTranscribe,
  cancelRecording,
} = useAudioRecorder();

const formatRecordDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const handleStartRecording = async () => {
  recordError.value = null;
  await startRecording();
};

const handleStopRecording = async () => {
  const tenantId = authStore.user?.tenant_id || `user_${authStore.user?.id || 0}`;
  const text = await stopAndTranscribe(tenantId, 'es-CO');
  if (text) {
    input.value = input.value ? `${input.value} ${text}` : text;
  }
};

// Nombre del documento activo en el contexto del chat
const activeDocumentName = computed(() => {
  if (!activeDocumentId.value) return null;
  const doc = sessionDocuments.value.find(d => d.id === activeDocumentId.value);
  return doc?.filename || `Documento #${activeDocumentId.value}`;
});

// Computed
const agentCategory = computed(() => agent.value?.category?.toLowerCase() || 'general');

const isFinancialAgent = computed(() =>
  ['financiero', 'financial', 'contable', 'contabilidad'].includes(agentCategory.value)
);

const supportsDocuments = computed(() => true); // Todos los agentes soportan documentos
const activeTasksCount = computed(() =>
  analysisTasks.value.filter((task) => ['pending', 'running'].includes(task.status)).length
);

const allowedFormats = computed(() => {
  if (isFinancialAgent.value) return ['xlsx', 'xls', 'csv', 'pdf'];
  return ['pdf', 'xlsx', 'xls', 'csv', 'docx', 'txt', 'png', 'jpg'];
});

const categoryIcon = computed(() => ({
  financiero: 'pi-chart-line',
  financial: 'pi-chart-line',
  contable: 'pi-chart-line',
  soporte: 'pi-headphones',
  ventas: 'pi-shopping-cart',
  legal: 'pi-book',
  general: 'pi-android',
}[agentCategory.value] || 'pi-android'));

const inputPlaceholder = computed(() => {
  if (isFinancialAgent.value) return 'Pregunta sobre los estados financieros, indicadores o normas contables...';
  return 'Escribe tu mensaje...';
});

const welcomeText = computed(() => {
  if (isFinancialAgent.value)
    return 'Soy tu analista financiero. Puedes subir estados financieros (Excel, PDF, CSV) y te ayudaré a calcular indicadores, detectar anomalías y verificar cumplimiento normativo (NIIF, NICSP, Código de Comercio).';
  return agent.value?.description || 'Estoy listo para ayudarte. ¿En qué puedo asistirte?';
});

// Utilidades
const scrollToBottom = async () => {
  await nextTick();
  if (messagesEl.value) messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
};

const now = () => new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

const docIcon = (type) => ({
  pdf: 'pi-file-pdf', xlsx: 'pi-file-excel', xls: 'pi-file-excel',
  csv: 'pi-table', docx: 'pi-file-word', txt: 'pi-file',
  png: 'pi-image', jpg: 'pi-image',
}[type] || 'pi-file');

const fileTypeFromName = (filename) => {
  const ext = String(filename || '').split('.').pop()?.toLowerCase();
  return ext || 'file';
};

const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(text));
  return div.innerHTML;
};

const renderMarkdown = (text) => {
  // Markdown básico: negrita, cursiva, código, listas, encabezados
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
};

const normalizeDownloadPath = (rawUrl) => {
  if (!rawUrl) return null;
  if (rawUrl.startsWith('/api/v1/')) return rawUrl.replace('/api/v1', '');
  if (rawUrl.startsWith('/')) return rawUrl;
  return `/${rawUrl}`;
};

const downloadFromChat = async (download) => {
  try {
    const path = normalizeDownloadPath(download.download_url);
    if (!path) throw new Error('URL de descarga inválida');

    const response = await api.get(path, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = download.filename || `reporte_${download.report_id || 'nexo'}.${download.format || 'pdf'}`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    const detail = e.response?.data?.detail || e.message || 'No se pudo descargar el archivo.';
    messages.value.push({
      text: `❌ Error de descarga: ${detail}`,
      isUser: false,
      time: now(),
    });
    await scrollToBottom();
  }
};

const taskStatusSeverity = (statusValue) => ({
  pending: 'warning',
  running: 'info',
  completed: 'success',
  failed: 'danger',
}[statusValue] || 'secondary');

const normalizeTaskEvent = (task) => ({
  taskId: task?.taskId || task?.task_id || '',
  documentId: task?.documentId || task?.document_id || null,
  filename: task?.filename || null,
  status: task?.status || 'pending',
  progressPercent: Number(task?.progressPercent ?? task?.progress_percent ?? 0),
  message: task?.message || '',
  errorMessage: task?.errorMessage || task?.error_message || null,
  result: task?.result || null,
  analysisRunId: task?.analysisRunId || task?.analysis_run_id || null,
  completedNotified: Boolean(task?.completedNotified),
  updatedAt: new Date().toISOString(),
});

const upsertTask = (rawTask) => {
  const normalized = normalizeTaskEvent(rawTask);
  if (!normalized.taskId) return null;

  const index = analysisTasks.value.findIndex((item) => item.taskId === normalized.taskId);
  if (index >= 0) {
    const previous = analysisTasks.value[index];
    analysisTasks.value[index] = {
      ...previous,
      ...normalized,
      completedNotified: previous.completedNotified || normalized.completedNotified,
    };
    return analysisTasks.value[index];
  }
  analysisTasks.value = [normalized, ...analysisTasks.value].slice(0, 12);
  return analysisTasks.value[0];
};

const applyTaskCompletion = async (task) => {
  if (!task || task.completedNotified || !task.result) return;
  task.completedNotified = true;
  onDocumentAnalyzed(task.result);
};

const pollActiveTasks = async () => {
  if (!isFinancialAgent.value) return;
  const active = analysisTasks.value.filter((task) => ['pending', 'running'].includes(task.status));
  if (!active.length) {
    if (tasksPollIntervalId) {
      clearInterval(tasksPollIntervalId);
      tasksPollIntervalId = null;
    }
    return;
  }

  await Promise.all(active.map(async (task) => {
    try {
      const latest = await financialService.getAnalysisTask(task.taskId);
      const updated = upsertTask(latest);
      if (updated?.status === 'completed') {
        await applyTaskCompletion(updated);
      }
    } catch (e) {
      // No interrumpir el chat por un error temporal de polling
    }
  }));
};

const ensureTasksPolling = () => {
  if (tasksPollIntervalId || !isFinancialAgent.value) return;
  tasksPollIntervalId = setInterval(() => {
    pollActiveTasks();
  }, 3000);
};

// Enviar mensaje
const sendMessage = async () => {
  const text = input.value.trim();
  if (!text || loading.value) return;

  input.value = '';
  messages.value.push({ text, isUser: true, time: now() });
  await scrollToBottom();
  loading.value = true;

  try {
    let res;

    if (isFinancialAgent.value) {
      // Usar el endpoint del agente financiero
      if (!financialSessionId.value) {
        financialSessionId.value = await financialService.newSession();
      }
      res = await financialService.chat(
        text,
        financialSessionId.value,
        null,                    // company_id
        activeDocumentId.value,  // document_id del último documento analizado
      );
    } else {
      // Agente genérico
      res = await agentsService.chatWithAgent(props.agentId, text, authStore.user?.tenant_id);
    }

    messages.value.push({
      text: res.response,
      isUser: false,
      tokens: res.tokens_used,
      downloads: res.data?.downloads || [],
      time: now(),
    });
  } catch (e) {
    const detail = e.response?.data?.detail || e.message || 'Error al procesar tu mensaje.';
    messages.value.push({
      text: `❌ ${detail}`,
      isUser: false,
      time: now(),
    });
  } finally {
    loading.value = false;
    await scrollToBottom();
  }
};

// Manejo de documentos
const onDocumentUploaded = (doc) => {
  sessionDocuments.value.push(doc);
  showUpload.value = false;
  messages.value.push({
    type: 'document',
    text: `Documento subido: ${doc.filename}`,
    fileType: doc.file_type,
    isUser: true,
    time: now(),
  });
  messages.value.push({
    text: `✅ Documento **${doc.filename}** subido correctamente (SHA-256: \`${doc.sha256_hash?.slice(0, 12)}...\`). Puedes preguntarme sobre su contenido.`,
    isUser: false,
    time: now(),
  });
  scrollToBottom();
};

const onDocumentAnalyzed = (result) => {
  const alreadyProcessed = sessionDocuments.value.some((doc) => doc.id === result.document_id && doc.status === 'processed');
  if (!alreadyProcessed) {
    sessionDocuments.value.push({ id: result.document_id, filename: result.filename, file_type: 'xlsx', status: 'processed' });
  }
  financialResults.value = result;
  showResultsDialog.value = true;
  activeDocumentId.value = result.document_id;  // ← guardar para el chat
  showUpload.value = false;

  if (alreadyProcessed) {
    scrollToBottom();
    return;
  }

  const critical = result.anomalies?.critical || 0;
  const score = result.compliance?.compliance_score || 0;
  const totalIndicators = Object.values(result.indicators || {}).flat().length;

  messages.value.push({
    type: 'document',
    text: `Analizado: ${result.filename}`,
    fileType: result.filename?.split('.').pop(),
    isUser: true,
    time: now(),
  });
  messages.value.push({
    text: `✅ **Análisis completado** para \`${result.filename}\`\n\n` +
      (result.period?.label ? `📅 **Período:** ${result.period.label}\n` : '') +
      `📊 **${totalIndicators}** indicadores calculados\n` +
      `⚠️ **${result.anomalies?.total || 0}** anomalías detectadas${critical ? ` (${critical} críticas)` : ''}\n` +
      `✅ Cumplimiento normativo: **${score.toFixed(1)}%**\n\n` +
      `Los resultados detallados aparecen arriba. ¿Quieres que explique algún indicador o anomalía específica?`,
    isUser: false,
    time: now(),
  });
  scrollToBottom();
};

const onTaskQueued = (task) => {
  const item = upsertTask(task);
  if (item) ensureTasksPolling();
};

const onTaskProgress = (task) => {
  const item = upsertTask(task);
  if (item && ['pending', 'running'].includes(item.status)) {
    ensureTasksPolling();
  }
};

const onTaskCompleted = async (task) => {
  const item = upsertTask(task);
  await applyTaskCompletion(item);
};

const onTaskFailed = async (task) => {
  const item = upsertTask(task);
  if (!item) return;
  messages.value.push({
    text: `❌ La tarea de análisis falló: ${item.errorMessage || item.message || 'Error desconocido.'}`,
    isUser: false,
    time: now(),
  });
  await scrollToBottom();
};

const onUploadError = (msg) => {
  messages.value.push({
    text: `❌ Error al subir el archivo: ${msg}`,
    isUser: false,
    time: now(),
  });
  scrollToBottom();
};

const openRepositorySearch = async () => {
  showRepositorySearch.value = true;
  if (!repoSearchResults.value.length) {
    await runRepositorySearch();
  }
};

const onRepositorySearchInput = () => {
  if (repositorySearchDebounceId) {
    clearTimeout(repositorySearchDebounceId);
  }
  repositorySearchDebounceId = setTimeout(() => {
    runRepositorySearch();
  }, 350);
};

const runRepositorySearch = async () => {
  if (!isFinancialAgent.value) return;
  repoSearchLoading.value = true;
  repoSearchError.value = '';
  try {
    repoSearchResults.value = await listRepositoryDocuments({
      search: repoSearchQuery.value || null,
      limit: 25,
    });
  } catch (e) {
    repoSearchError.value = e.response?.data?.detail || e.message || 'No se pudo buscar documentos.';
  } finally {
    repoSearchLoading.value = false;
  }
};

const useRepositoryDocument = async (repositoryDoc) => {
  selectingRepositoryDocumentId.value = repositoryDoc.document_id;
  repoSearchError.value = '';
  try {
    const context = await resolveRepositoryDocumentChatContext({
      documentId: repositoryDoc.document_id,
    });

    activeDocumentId.value = context.document_id;

    const exists = sessionDocuments.value.some((doc) => doc.id === context.document_id);
    if (!exists) {
      sessionDocuments.value.push({
        id: context.document_id,
        filename: context.filename,
        file_type: fileTypeFromName(context.filename),
        status: 'processed',
      });
    }

    showRepositorySearch.value = false;
    messages.value.push({
      text:
        `✅ Documento activo desde Centro Documental: **${context.title}** (#${context.repository_document_id})\n` +
        `Archivo: ${context.filename}\nPeríodo: ${context.period_label}`,
      isUser: false,
      time: now(),
    });
    await scrollToBottom();
  } catch (e) {
    repoSearchError.value = e.response?.data?.detail || e.message || 'No se pudo activar el documento.';
  } finally {
    selectingRepositoryDocumentId.value = null;
  }
};

const onTranscribed = (text) => {
  // Legacy callback kept for compatibility; dictation now uses useAudioRecorder inline.
  input.value = text;
};

onMounted(async () => {
  try {
    agent.value = await agentsService.getAgentById(props.agentId);
    ensureTasksPolling();
  } catch (e) {
    console.error(e);
  }
});

onUnmounted(() => {
  if (tasksPollIntervalId) {
    clearInterval(tasksPollIntervalId);
    tasksPollIntervalId = null;
  }
  if (repositorySearchDebounceId) {
    clearTimeout(repositorySearchDebounceId);
    repositorySearchDebounceId = null;
  }
});
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - 84px);
  min-height: 620px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

/* Header */
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  position: sticky;
  top: 0;
  z-index: 5;
}
.header-left { display: flex; align-items: center; gap: 0.875rem; }
.header-right { display: flex; align-items: center; gap: 0.5rem; }
.agent-avatar {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.25rem; flex-shrink: 0;
}
.agent-avatar.financiero, .agent-avatar.financial, .agent-avatar.contable { background: #dbeafe; color: #1d4ed8; }
.agent-avatar.soporte { background: #dcfce7; color: #15803d; }
.agent-avatar.ventas { background: #fef3c7; color: #b45309; }
.agent-avatar.general { background: #f3e8ff; color: #7c3aed; }
.chat-header h2 { margin: 0; font-size: 1rem; color: #1e293b; }
.agent-desc { font-size: 0.78rem; color: #6b7280; }

/* Contexto */
.context-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}
.context-item {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #334155;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.78rem;
}
.context-item-success {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #166534;
}

.tasks-strip {
  padding: 0.65rem 1.25rem 0.8rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}
.tasks-strip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.tasks-strip-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}
.tasks-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 140px;
  overflow-y: auto;
}
.task-item {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.45rem 0.55rem;
  background: #f8fafc;
}
.task-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
}
.task-file {
  font-size: 0.78rem;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.task-sub {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  color: #64748b;
  margin: 0.2rem 0 0.35rem;
}

.session-docs { margin-top: 0.75rem; }
.session-docs-title { font-size: 0.8rem; color: #6b7280; margin-bottom: 0.4rem; }
.session-doc-item {
  display: flex; align-items: center; gap: 0.5rem;
  font-size: 0.82rem; color: #374151; padding: 0.3rem 0;
}

/* Mensajes */
.messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.welcome-msg {
  display: flex; align-items: flex-start; gap: 0.875rem;
  padding: 1rem; background: #f8fafc; border-radius: 12px;
  border: 1px solid #e2e8f0;
}
.welcome-avatar {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; flex-shrink: 0;
}
.welcome-avatar.financiero, .welcome-avatar.financial { background: #dbeafe; color: #1d4ed8; }
.welcome-avatar.general { background: #f3e8ff; color: #7c3aed; }
.welcome-text strong { color: #1e293b; }
.welcome-text p { margin: 0.25rem 0 0; font-size: 0.875rem; color: #6b7280; line-height: 1.5; }

.message { display: flex; flex-direction: column; }
.message.user { align-items: flex-end; }
.message.agent { align-items: flex-start; }

.bubble {
  max-width: min(86%, 860px);
  padding: 0.75rem 1rem;
  border-radius: 16px;
  line-height: 1.6;
  font-size: 0.9rem;
  word-break: break-word;
}
.user .bubble {
  background: #6366f1;
  color: white;
  border-bottom-right-radius: 4px;
}
.agent .bubble {
  background: #f1f5f9;
  color: #1e293b;
  border-bottom-left-radius: 4px;
}
.agent .bubble :deep(h2), .agent .bubble :deep(h3), .agent .bubble :deep(h4) {
  margin: 0.5rem 0 0.25rem; color: #1e293b;
}
.agent .bubble :deep(ul) { padding-left: 1.25rem; margin: 0.25rem 0; }
.agent .bubble :deep(code) {
  background: #e2e8f0; padding: 0.1rem 0.3rem; border-radius: 4px;
  font-family: monospace; font-size: 0.85em;
}
.agent .bubble :deep(strong) { color: #1e293b; }

.chat-downloads {
  margin-top: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.chat-download-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: fit-content;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #1e293b;
  font-size: 0.8rem;
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  cursor: pointer;
}

.chat-download-btn:hover {
  border-color: #6366f1;
  color: #4338ca;
}

.doc-bubble {
  display: flex; align-items: center; gap: 0.5rem;
  background: #eff6ff; color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.meta {
  display: flex; gap: 0.5rem;
  font-size: 0.72rem; color: #9ca3af;
  margin-top: 0.25rem; padding: 0 0.25rem;
}

/* Typing indicator */
.typing {
  display: flex; align-items: center; gap: 4px;
  padding: 0.75rem 1rem;
}
.dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #9ca3af;
  animation: bounce 1.2s infinite;
}
.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

/* Input */
.chat-input {
  padding: 0.875rem 1.25rem;
  border-top: 1px solid #e5e7eb;
  background: white;
  position: sticky;
  bottom: 0;
  z-index: 6;
}

/* Recording indicators */
.recording-indicator {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 0.82rem;
  color: #334155;
}
.recording-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ef4444;
  animation: rec-pulse 1s infinite;
}
@keyframes rec-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.recording-cancel {
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 0.15rem 0.3rem;
  border-radius: 4px;
  line-height: 1;
  margin-left: auto;
}
.recording-cancel:hover { color: #ef4444; background: #fef2f2; }
.record-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 0.8rem;
}
.record-error-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #fca5a5;
  padding: 0.1rem 0.25rem;
  border-radius: 4px;
  line-height: 1;
  margin-left: auto;
}
.record-error-close:hover { color: #b91c1c; }

/* Barra de documento activo */
.active-doc-bar {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.4rem 0.75rem; margin-bottom: 0.5rem;
  background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px;
  font-size: 0.8rem; color: #1d4ed8;
}
.active-doc-bar .pi-file-excel { color: #16a34a; }
.active-doc-bar span { flex: 1; }
.clear-doc {
  background: none; border: none; cursor: pointer; padding: 0.1rem 0.3rem;
  color: #93c5fd; border-radius: 4px; line-height: 1;
}
.clear-doc:hover { background: #dbeafe; color: #1d4ed8; }
.input-row { display: flex; align-items: flex-end; gap: 0.5rem; }
.chat-textarea {
  flex: 1;
  resize: none;
  border-radius: 12px;
  font-size: 0.9rem;
  max-height: 120px;
  overflow-y: auto;
}
.input-actions { display: flex; align-items: center; gap: 0.25rem; }
.input-hint { font-size: 0.72rem; color: #9ca3af; margin-top: 0.4rem; text-align: center; }

.repo-search {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.repo-search__bar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.repo-search__input {
  flex: 1;
}

.repo-search__results {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  max-height: 52vh;
  overflow-y: auto;
}

.repo-doc-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.7rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.repo-doc-item__meta {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.repo-doc-item__meta strong,
.repo-doc-item__meta span,
.repo-doc-item__meta small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.repo-doc-item__meta span {
  font-size: 0.84rem;
  color: #475569;
}

.repo-doc-item__meta small {
  font-size: 0.75rem;
  color: #64748b;
}

.repo-search__hint {
  margin: 0;
  color: #64748b;
  font-size: 0.84rem;
}

.repo-search__error {
  margin: 0;
  color: #b91c1c;
  font-size: 0.84rem;
}

/* Transiciones */
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.25s ease; }
.slide-down-enter-from, .slide-down-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 768px) {
  .chat-container {
    height: calc(100dvh - 70px);
    min-height: unset;
    border-radius: 0;
  }
  .chat-header {
    padding: 0.75rem;
  }
  .messages {
    padding: 0.8rem;
  }
  .bubble {
    max-width: 94%;
    font-size: 0.88rem;
  }
  .chat-input {
    padding: 0.7rem;
  }
  .tasks-strip {
    padding: 0.55rem 0.7rem;
  }
  .tasks-list {
    max-height: 120px;
  }
  .repo-search__bar {
    flex-direction: column;
    align-items: stretch;
  }
  .repo-doc-item {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
